import React from 'react';
import { View, Text, ViewStyle, TextStyle } from 'react-native';
import { useTheme } from '../context/themeContext';

export type ItemState = 'DRAFT' | 'PUBLISHED' | 'DEACTIVATED' | 'ARCHIVED' | 'SCHEDULED';
export type StateBadgeSize = 'default' | 'lg';

interface StateBadgeProps {
  state: ItemState;
  size?: StateBadgeSize;
  style?: ViewStyle;
}

interface StateMeta {
  label: string;
  tone: 'neutral' | 'success' | 'danger' | 'outline' | 'info';
}

const STATE_META: Record<ItemState, StateMeta> = {
  DRAFT:       { label: 'Draft',       tone: 'neutral' },
  PUBLISHED:   { label: 'Published',   tone: 'success' },
  DEACTIVATED: { label: 'Deactivated', tone: 'danger'  },
  ARCHIVED:    { label: 'Archived',    tone: 'outline' },
  SCHEDULED:   { label: 'Scheduled',   tone: 'info'    },
};

export default function StateBadge({ state, size = 'default', style }: StateBadgeProps) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii } = theme;

  const meta = STATE_META[state] ?? STATE_META.DRAFT;

  type ToneColors = { bg: string; border: string; color: string; dotColor: string };

  const toneMap: Record<string, ToneColors> = {
    info:    { bg: colors.infoSoft,    border: colors.infoLine,    color: colors.info,    dotColor: colors.info },
    success: { bg: colors.successSoft, border: colors.successLine, color: colors.success, dotColor: colors.success },
    warn:    { bg: colors.warnSoft,    border: colors.warnLine,    color: colors.warn,    dotColor: colors.warn },
    danger:  { bg: colors.dangerSoft,  border: colors.dangerLine,  color: colors.danger,  dotColor: colors.danger },
    neutral: { bg: colors.neutralSoft, border: colors.neutralLine, color: colors.neutral, dotColor: colors.neutral },
    outline: { bg: colors.transparent, border: colors.line,        color: colors.ink2,    dotColor: colors.ink4 },
  };

  const tc: ToneColors = toneMap[meta.tone] ?? toneMap.neutral;

  const height = size === 'lg' ? 26 : 22;
  const hPad = size === 'lg' ? 12 : 9;
  const fSize = size === 'lg' ? fontSize.sm : fontSize.xs;

  const containerStyle: ViewStyle = {
    height,
    paddingHorizontal: hPad,
    borderRadius: radii.full,
    borderWidth: 1,
    borderColor: tc.border,
    backgroundColor: tc.bg,
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 5,
  };

  const labelStyle: TextStyle = {
    fontSize: fSize,
    fontWeight: fontWeight.medium,
    color: tc.color,
    fontFamily: theme.font.sans,
    includeFontPadding: false,
  };

  return (
    <View style={[containerStyle, style]}>
      <View
        style={{
          width: 7,
          height: 7,
          borderRadius: 3.5,
          backgroundColor: tc.dotColor,
        }}
      />
      <Text style={labelStyle}>{meta.label}</Text>
    </View>
  );
}
